import dayjs from 'dayjs';

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Verifica se um horário específico está dentro dos horários permitidos para campanhas
 * @param {Object} campaignHours - Configuração dos horários das campanhas
 * @param {dayjs.Dayjs} dateTime - Data e hora a ser verificada
 * @returns {boolean} - True se o horário está permitido
 */
export const isValidCampaignTime = (campaignHours, dateTime) => {
  if (!campaignHours || !dateTime) return false;
  
  const dayOfWeek = DAYS_OF_WEEK[dateTime.day()];
  const dayConfig = campaignHours[dayOfWeek];
  
  if (!dayConfig) return false;
  
  // Se sempre aberto, qualquer horário é válido
  if (dayConfig.always_open) return true;
  
  // Se não tem intervalos configurados, não é válido
  if (!dayConfig.intervals || dayConfig.intervals.length === 0) return false;
  
  const currentTime = dateTime.format('HH:mm');
  
  // Verifica se o horário atual está dentro de algum intervalo
  return dayConfig.intervals.some(interval => {
    return currentTime >= interval.start && currentTime <= interval.end;
  });
};

/**
 * Encontra o próximo horário válido para envio de campanha
 * @param {Object} campaignHours - Configuração dos horários das campanhas
 * @param {dayjs.Dayjs} startFrom - Data/hora de início da busca (default: agora)
 * @returns {dayjs.Dayjs|null} - Próximo horário válido ou null se não encontrar
 */
export const getNextValidCampaignTime = (campaignHours, startFrom = dayjs()) => {
  if (!campaignHours) return null;
  
  let currentDateTime = startFrom.clone();
  const maxDaysToCheck = 14; // Evita loop infinito
  
  for (let daysChecked = 0; daysChecked < maxDaysToCheck; daysChecked++) {
    const dayOfWeek = DAYS_OF_WEEK[currentDateTime.day()];
    const dayConfig = campaignHours[dayOfWeek];
    
    if (dayConfig) {
      // Se sempre aberto, retorna o horário atual (se for hoje) ou início do dia
      if (dayConfig.always_open) {
        if (daysChecked === 0) {
          return currentDateTime;
        } else {
          return currentDateTime.startOf('day');
        }
      }
      
      // Se tem intervalos configurados
      if (dayConfig.intervals && dayConfig.intervals.length > 0) {
        for (const interval of dayConfig.intervals) {
          const startTime = currentDateTime.clone()
            .hour(parseInt(interval.start.split(':')[0]))
            .minute(parseInt(interval.start.split(':')[1]))
            .second(0);
          
          const endTime = currentDateTime.clone()
            .hour(parseInt(interval.end.split(':')[0]))
            .minute(parseInt(interval.end.split(':')[1]))
            .second(0);
          
          // Se estamos verificando o dia atual
          if (daysChecked === 0) {
            // Se ainda estamos dentro do intervalo
            if (currentDateTime.isBefore(endTime) && currentDateTime.isAfter(startTime)) {
              return currentDateTime;
            }
            // Se o intervalo ainda não começou hoje
            if (currentDateTime.isBefore(startTime)) {
              return startTime;
            }
          } else {
            // Para dias futuros, retorna o início do primeiro intervalo
            return startTime;
          }
        }
      }
    }
    
    // Passa para o próximo dia
    currentDateTime = currentDateTime.add(1, 'day').startOf('day');
  }
  
  return null; // Não encontrou horário válido
};

/**
 * Verifica se o horário atual está dentro dos horários permitidos
 * @param {Object} campaignHours - Configuração dos horários das campanhas
 * @returns {boolean} - True se agora é um horário válido
 */
export const isCurrentTimeValid = (campaignHours) => {
  return isValidCampaignTime(campaignHours, dayjs());
};

/**
 * Formata um intervalo de horário para exibição
 * @param {Object} interval - { start: 'HH:mm', end: 'HH:mm' }
 * @returns {string} - Horário formatado
 */
export const formatTimeInterval = (interval) => {
  return `${interval.start} às ${interval.end}`;
};

/**
 * Obtém uma descrição dos horários permitidos para um dia específico
 * @param {Object} dayConfig - Configuração do dia
 * @param {string} dayName - Nome do dia
 * @returns {string} - Descrição dos horários
 */
export const getDayScheduleDescription = (dayConfig, dayName) => {
  if (!dayConfig) return `${dayName}: Não configurado`;
  
  if (dayConfig.always_open) return `${dayName}: 24 horas`;
  
  if (!dayConfig.intervals || dayConfig.intervals.length === 0) {
    return `${dayName}: Indisponível`;
  }
  
  const intervals = dayConfig.intervals.map(formatTimeInterval).join(', ');
  return `${dayName}: ${intervals}`;
}; 